import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { FEEDBACK_CATEGORIES, FEEDBACK_CATEGORY_LABELS, FEEDBACK_LIMITS, type FeedbackCategory } from "@talenthop/shared";
import { ApiError, api } from "../lib/api.js";
import { Bug } from "./icons.js";

/**
 * The "Feedback" control in the app header (both consoles). One press opens a small dialog; one
 * note lands in product_feedback (migration 0081) with the route it was written on, and the
 * platform console reads it under Product feedback. It never leaves the page the person is on.
 */
export function FeedbackButton({
  variant = "default",
}: {
  variant?: "default" | "customer";
} = {}): ReactNode {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<FeedbackCategory>("idea");
  const [message, setMessage] = useState("");
  const [rating, setRating] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const close = (): void => {
    setOpen(false);
    setError(null);
    setBusy(false);
  };

  // Escape closes; a sent note closes itself after a beat so the thanks is actually seen.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEffect(() => {
    if (!sent) return;
    const t = window.setTimeout(() => {
      setSent(false);
      setMessage("");
      setRating(null);
      setCategory("idea");
      close();
    }, 1600);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sent]);

  const trimmed = message.trim();
  const canSend = trimmed.length >= FEEDBACK_LIMITS.minMessage && trimmed.length <= FEEDBACK_LIMITS.maxMessage && !busy;

  const send = async (): Promise<void> => {
    if (!canSend) return;
    setBusy(true);
    setError(null);
    try {
      await api.post("/feedback", {
        category,
        message: trimmed,
        ...(rating ? { rating } : {}),
        pagePath: pathname.slice(0, FEEDBACK_LIMITS.maxPagePath),
      });
      setSent(true);
    } catch (e) {
      const body = e instanceof ApiError ? (e.body as { message?: string } | undefined) : undefined;
      setError(
        e instanceof ApiError && e.status === 503
          ? (body?.message ?? "Feedback is not available yet — please try again in a few minutes.")
          : "That didn't send. Please try again in a moment.",
      );
      setBusy(false);
    }
  };

  return (
    <>
      {variant === "customer" ? (
        <button
          type="button"
          className="customer-feedback-btn"
          data-tip="Send feedback"
          aria-label="Send feedback"
          onClick={() => setOpen(true)}
        >
          <Bug className="btn-icon" />
        </button>
      ) : (
        <button
          type="button"
          className="theme-toggle feedback-btn"
          data-tip="Send feedback"
          aria-label="Send feedback"
          onClick={() => setOpen(true)}
        >
          <Bug />
        </button>
      )}

      {open && (
        <div className="rq-modal-scrim" role="dialog" aria-modal="true" aria-labelledby="fb-title" onClick={(e) => e.target === e.currentTarget && close()}>
          <div className="rq-modal fb-modal t1s-fb">
            <div className="rq-modal-h">
              <div className="t" id="fb-title">What should we improve?</div>
              <div className="d">
                A person on the TalentHop team reads every note. This one is tagged with the page you are on
                (<code>{pathname}</code>).
              </div>
            </div>

            {sent ? (
              <div className="rq-modal-b">
                <div className="banner ok" style={{ margin: "6px 0 14px" }}>
                  <span>Thank you — your note is with the team.</span>
                </div>
              </div>
            ) : (
              <>
                <div className="rq-modal-b">
                  <div className="rq-chips" role="radiogroup" aria-label="What kind of note is this?">
                    {FEEDBACK_CATEGORIES.map((c) => (
                      <button
                        key={c}
                        type="button"
                        role="radio"
                        aria-checked={category === c}
                        className={`rq-chip${category === c ? " on" : ""}`}
                        onClick={() => setCategory(c)}
                      >
                        {FEEDBACK_CATEGORY_LABELS[c]}
                      </button>
                    ))}
                  </div>

                  <label className="rq-lab" htmlFor="fb-message" style={{ marginTop: 12 }}>
                    Your note <span className="h">— what happened, or what you wish it did</span>
                  </label>
                  <textarea
                    id="fb-message"
                    className="rq-in"
                    rows={5}
                    value={message}
                    maxLength={FEEDBACK_LIMITS.maxMessage}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="e.g. On the report I can't tell which weights were used without opening the request."
                    autoFocus
                  />

                  <div className="fb-rating" aria-label="How is TalentHop going for you? (optional)">
                    <span className="mut sm">How is it going for you?</span>
                    <div className="fb-stars" role="radiogroup">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button
                          key={n}
                          type="button"
                          role="radio"
                          aria-checked={rating === n}
                          aria-label={`${n} of 5`}
                          className={`fb-star${rating !== null && n <= rating ? " on" : ""}`}
                          onClick={() => setRating(rating === n ? null : n)}
                        >
                          ★
                        </button>
                      ))}
                      <span className="mut sm">{rating ? `${rating} / 5` : "optional"}</span>
                    </div>
                  </div>

                  {error && (
                    <div className="banner crit" style={{ marginTop: 10 }}>
                      <span>{error}</span>
                    </div>
                  )}
                </div>
                <div className="rq-modal-f">
                  <button type="button" className="rq-btn ghost" onClick={close} disabled={busy}>
                    Cancel
                  </button>
                  <button type="button" className="rq-btn primary" onClick={() => void send()} disabled={!canSend}>
                    {busy ? "Sending…" : "Send feedback"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
